import React from "react";
import { ErrorMessage, Form as FormFormik, Formik, FormikHelpers } from "formik";
import { Row, Col, Form, Container } from "react-bootstrap";
import { Trans, useTranslation } from "react-i18next";
import { CustomModal } from "@services/ui-components";
import { getServiceProviderSettingsValidationSchema } from "../utils/serviceProviderSettingsValidation";
import { UpdateEmployeeServiceRelationsOptionsType } from "@me-team/host/main/graphql/types";

export type ServiceProviderUpdateOption = UpdateEmployeeServiceRelationsOptionsType | "KEEP_OLD_VALUE";

interface ServiceProviderSettingsModalProps {
    show: boolean;
    onClose: () => void;
    onApply: (selections: {
        duration?: { option: ServiceProviderUpdateOption };
        price?: { option: ServiceProviderUpdateOption };
        online?: { option: ServiceProviderUpdateOption };
    }) => void;
    changes: {
        duration?: { oldValue: string; newValue: string; diff: string };
        price?: { oldValue: number; newValue: number; diff: number };
        online?: { oldValue: boolean; newValue: boolean };
    };
    serviceName?: string;
}

export interface ServiceProviderSettingsFormValues {
    durationOption: ServiceProviderUpdateOption | "";
    priceOption: ServiceProviderUpdateOption | "";
    onlineOption: ServiceProviderUpdateOption | "";
}

const ServiceProviderSettingsModal: React.FC<ServiceProviderSettingsModalProps> = ({
    show,
    onClose,
    onApply,
    changes,
    serviceName,
}) => {
    const { t } = useTranslation();

    const hasDurationChanges = !!changes.duration;
    const hasPriceChanges = !!changes.price;
    const hasOnlineChanges = !!changes.online;

    const initialValues: ServiceProviderSettingsFormValues = {
        durationOption: "",
        priceOption: "",
        onlineOption: "",
    };

    const handleSubmit = (
        values: ServiceProviderSettingsFormValues,
        actions: FormikHelpers<ServiceProviderSettingsFormValues>
    ) => {
        const selections: {
            duration?: { option: ServiceProviderUpdateOption };
            price?: { option: ServiceProviderUpdateOption };
            online?: { option: ServiceProviderUpdateOption };
        } = {};

        if (hasDurationChanges && values.durationOption !== "") {
            selections.duration = { option: values.durationOption };
        }
        if (hasPriceChanges && values.priceOption !== "") {
            selections.price = { option: values.priceOption };
        }
        if (hasOnlineChanges && values.onlineOption !== "") {
            selections.online = { option: values.onlineOption };
        }

        onApply(selections);
        actions.setSubmitting(false);
    };

    return (
        <CustomModal
            show={show}
            handleClose={onClose}
            title={t("Change service parameters for performers?")}
            headerAlignment="start"
            bodyAlignment="start"
            body={
                <Formik<ServiceProviderSettingsFormValues>
                    initialValues={initialValues}
                    validationSchema={getServiceProviderSettingsValidationSchema(
                        t,
                        hasDurationChanges,
                        hasPriceChanges,
                        hasOnlineChanges
                    )}
                    onSubmit={handleSubmit}
                >
                    {({ handleSubmit, values, setFieldValue }) => (
                        <FormFormik id="service-provider-settings-form" onSubmit={handleSubmit}>
                            <Container>
                                <Row>
                                    {hasDurationChanges && changes.duration && (
                                        <Col xs={12} className="pb-4 px-0 text-dark">
                                            <Row className="mb-4">
                                                <Col xs={12}>
                                                    <p className="mb-0">
                                                        <Trans
                                                            i18nKey="You changed the duration of the service from {oldValue} to {newValue} ({diff})"
                                                            values={{
                                                                oldValue: changes.duration.oldValue,
                                                                newValue: changes.duration.newValue,
                                                                diff: changes.duration.diff,
                                                            }}
                                                            components={{ b: <strong /> }}
                                                        />
                                                    </p>
                                                    <p className="mb-0">
                                                        {t("How to apply changes for performers of the service?")}
                                                    </p>
                                                    <ErrorMessage name="durationOption">
                                                        {(msg) => <div className="invalid-feedback d-block">{msg}</div>}
                                                    </ErrorMessage>
                                                </Col>
                                            </Row>

                                            <Row className="mb-3">
                                                <Col xs={12}>
                                                    <Form.Check
                                                        type="radio"
                                                        id="duration-set-new"
                                                        name="durationOption"
                                                        label={t(
                                                            "Set the service duration to {duration} for all performers",
                                                            {
                                                                duration: changes.duration.newValue,
                                                            }
                                                        )}
                                                        checked={
                                                            values.durationOption ===
                                                            UpdateEmployeeServiceRelationsOptionsType.SetAbsolute
                                                        }
                                                        onChange={() =>
                                                            setFieldValue(
                                                                "durationOption",
                                                                UpdateEmployeeServiceRelationsOptionsType.SetAbsolute
                                                            )
                                                        }
                                                    />
                                                </Col>
                                            </Row>

                                            <Row className="mb-3">
                                                <Col xs={12}>
                                                    <Form.Check
                                                        type="radio"
                                                        id="duration-adjust"
                                                        name="durationOption"
                                                        label={t(
                                                            "Change the service duration by {diff} for all performers",
                                                            {
                                                                diff: changes.duration.diff,
                                                            }
                                                        )}
                                                        checked={
                                                            values.durationOption ===
                                                            UpdateEmployeeServiceRelationsOptionsType.AddDelta
                                                        }
                                                        onChange={() =>
                                                            setFieldValue(
                                                                "durationOption",
                                                                UpdateEmployeeServiceRelationsOptionsType.AddDelta
                                                            )
                                                        }
                                                    />
                                                </Col>
                                            </Row>

                                            <Row className="mb-3">
                                                <Col xs={12}>
                                                    <Form.Check
                                                        type="radio"
                                                        id="duration-keep"
                                                        name="durationOption"
                                                        label={t(
                                                            "Do not change the duration of the service for performers"
                                                        )}
                                                        checked={values.durationOption === "KEEP_OLD_VALUE"}
                                                        onChange={() =>
                                                            setFieldValue("durationOption", "KEEP_OLD_VALUE")
                                                        }
                                                    />
                                                </Col>
                                            </Row>
                                        </Col>
                                    )}

                                    {hasPriceChanges && changes.price && (
                                        <Col xs={12} className="py-4 px-0 text-dark border-top border-secondary">
                                            <Row className="mb-4">
                                                <Col xs={12}>
                                                    <p className="m-0">
                                                        <Trans
                                                            i18nKey="You changed the price of the service from {oldValue} to {newValue} ({diff})"
                                                            values={{
                                                                oldValue: changes.price.oldValue,
                                                                newValue: changes.price.newValue,
                                                                diff: changes.price.diff,
                                                            }}
                                                            components={{ b: <strong /> }}
                                                        />
                                                    </p>
                                                    <p className="m-0">
                                                        {t("How to apply changes for performers of the service?")}
                                                    </p>
                                                    <ErrorMessage name="priceOption">
                                                        {(msg) => <div className="invalid-feedback d-block">{msg}</div>}
                                                    </ErrorMessage>
                                                </Col>
                                            </Row>

                                            <Row className="mb-3">
                                                <Col xs={12}>
                                                    <Form.Check
                                                        type="radio"
                                                        id="price-set-new"
                                                        name="priceOption"
                                                        label={t(
                                                            "Set the service price to {price} for all performers",
                                                            {
                                                                price: changes.price.newValue,
                                                            }
                                                        )}
                                                        checked={
                                                            values.priceOption ===
                                                            UpdateEmployeeServiceRelationsOptionsType.SetAbsolute
                                                        }
                                                        onChange={() =>
                                                            setFieldValue(
                                                                "priceOption",
                                                                UpdateEmployeeServiceRelationsOptionsType.SetAbsolute
                                                            )
                                                        }
                                                    />
                                                </Col>
                                            </Row>

                                            <Row className="mb-3">
                                                <Col xs={12}>
                                                    <Form.Check
                                                        type="radio"
                                                        id="price-adjust"
                                                        name="priceOption"
                                                        label={t(
                                                            "Change the service price by {diff} for all performers",
                                                            {
                                                                diff: changes.price.diff,
                                                            }
                                                        )}
                                                        checked={
                                                            values.priceOption ===
                                                            UpdateEmployeeServiceRelationsOptionsType.AddDelta
                                                        }
                                                        onChange={() =>
                                                            setFieldValue(
                                                                "priceOption",
                                                                UpdateEmployeeServiceRelationsOptionsType.AddDelta
                                                            )
                                                        }
                                                    />
                                                </Col>
                                            </Row>

                                            <Row>
                                                <Col xs={12}>
                                                    <Form.Check
                                                        type="radio"
                                                        id="price-keep"
                                                        name="priceOption"
                                                        label={t(
                                                            "Do not change the price of the service for performers"
                                                        )}
                                                        checked={values.priceOption === "KEEP_OLD_VALUE"}
                                                        onChange={() => setFieldValue("priceOption", "KEEP_OLD_VALUE")}
                                                    />
                                                </Col>
                                            </Row>
                                        </Col>
                                    )}

                                    {hasOnlineChanges && changes.online && (
                                        <Col xs={12} className="pt-4 px-0 border-top border-secondary text-dark">
                                            <Row className="mb-4">
                                                <Col xs={12}>
                                                    <p className="m-0">
                                                        {changes.online.newValue
                                                            ? t(
                                                                  "You have enabled online booking for the service {serviceName}",
                                                                  { serviceName }
                                                              )
                                                            : t(
                                                                  "You have disabled online booking for the service {serviceName}",
                                                                  { serviceName }
                                                              )}
                                                    </p>
                                                    <p className="m-0">
                                                        {t("Apply online booking setting for all performers?")}
                                                    </p>
                                                    <ErrorMessage name="onlineOption">
                                                        {(msg) => <div className="invalid-feedback d-block">{msg}</div>}
                                                    </ErrorMessage>
                                                </Col>
                                            </Row>

                                            <Row className="mb-3">
                                                <Col xs={12}>
                                                    <Form.Check
                                                        type="radio"
                                                        id="online-enable-all"
                                                        name="onlineOption"
                                                        label={
                                                            changes.online.newValue
                                                                ? t(
                                                                      "Enable online booking for the service for all performers"
                                                                  )
                                                                : t(
                                                                      "Disable online booking for the service for all performers"
                                                                  )
                                                        }
                                                        checked={
                                                            values.onlineOption ===
                                                            UpdateEmployeeServiceRelationsOptionsType.SetAbsolute
                                                        }
                                                        onChange={() =>
                                                            setFieldValue(
                                                                "onlineOption",
                                                                UpdateEmployeeServiceRelationsOptionsType.SetAbsolute
                                                            )
                                                        }
                                                    />
                                                </Col>
                                            </Row>

                                            <Row>
                                                <Col xs={12}>
                                                    <Form.Check
                                                        type="radio"
                                                        id="online-keep"
                                                        name="onlineOption"
                                                        label={t(
                                                            "Do not change online booking settings for performers of this service"
                                                        )}
                                                        checked={values.onlineOption === "KEEP_OLD_VALUE"}
                                                        onChange={() => setFieldValue("onlineOption", "KEEP_OLD_VALUE")}
                                                    />
                                                </Col>
                                            </Row>
                                        </Col>
                                    )}
                                    <Col xs={12}>
                                        <button type="submit" style={{ display: "none" }} />
                                    </Col>
                                </Row>
                            </Container>
                        </FormFormik>
                    )}
                </Formik>
            }
            buttons={[
                {
                    text: t("cancel"),
                    onClick: onClose,
                    variant: "outline-primary",
                    className: "w-100",
                },
                {
                    text: t("apply"),
                    onClick: () => {
                        const form = document.getElementById("service-provider-settings-form") as HTMLFormElement;
                        if (form) form.requestSubmit();
                    },
                    variant: "primary",
                    className: "w-100",
                    type: "submit" as const,
                },
            ]}
        />
    );
};

export default ServiceProviderSettingsModal;
